import { AgGridReact } from '@ag-grid-community/react'
import {
  GridReadyEvent,
  IServerSideGetRowsParams,
} from '@ag-grid-community/core'
import { selectors as authSelectors } from '@hixme/auth-ui'
import { Stack } from '@sureco/design-system'
import { useQueryClient } from '@tanstack/react-query'
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useSelector } from 'react-redux'
import { getCensusUploadQueue } from '../api/census-enrollment-platform-api-client'
import {
  CensusUploadJobChannel,
  CensusUploadJobDto,
} from '../api/dto/census-upload-job.dto'
import { DEFAULT_FILES_PAGINATION_LIMIT } from '../constants'
import { filterClients } from '../CensusReview/utils/filter-clients'
import {
  CENSUS_UPLOAD_QUEUE_COUNT_QUERY_KEY,
  useCensusUploadQueueCount,
} from '../hooks/useCensusUploadQueueCount'
import { useCensusV2Clients } from '../hooks/use-census-v2-clients'
import { CensusClientFilter } from '../types/census-client-filter'
import {
  ActiveUploadsSnapshot,
  buildCensusUploadQueueFilters,
  CENSUS_QUEUE_POLL_INTERVAL_MS,
  getActiveUploadIds,
  hasActiveUploads,
  hasFinishedUploadsOnPage,
} from '../utils/census-upload-job'
import { getCensusQueueColDefs } from '../utils/get-census-queue-col-defs'

type Props = {
  clientNameLookup: Record<string, string>
  refreshKey: boolean
  filter?: CensusClientFilter
  channel?: CensusUploadJobChannel[]
  // Called once per refresh in which any running job finished, however many did
  onJobsFinished?: () => void
  showAction?: boolean
  showChannel?: boolean
}

// Census Manage: uploads that are queued, processing or failed. Hidden when empty
const CensusQueueGrid = ({
  clientNameLookup,
  refreshKey,
  filter,
  channel,
  onJobsFinished,
  showAction = false,
  showChannel = false,
}: Props) => {
  const [grid, setGrid] = useState<GridReadyEvent>()
  const [isPolling, setIsPolling] = useState(false)
  const activeUploadsRef = useRef<ActiveUploadsSnapshot>()
  const queryClient = useQueryClient()
  const { clients } = useCensusV2Clients()
  const role = useSelector(authSelectors.getRole) as string
  const filters = useMemo(
    () =>
      buildCensusUploadQueueFilters(
        filterClients(clients, filter, role).map((client) => client.id),
        filter,
        channel
      ),
    [clients, filter, role, channel]
  )
  const { data: total = 0, refetch } = useCensusUploadQueueCount({
    filters,
    refreshKey,
  })

  const datasource = useMemo(
    () => ({
      getRows: async (params: IServerSideGetRowsParams<CensusUploadJobDto>) => {
        const limit = DEFAULT_FILES_PAGINATION_LIMIT
        try {
          const page = Math.floor((params.request.startRow ?? 1) / limit) + 1
          const { meta, data } = await getCensusUploadQueue({
            ...filters,
            page,
            limit,
          })
          params.success({ rowData: data, rowCount: meta.total })
          setIsPolling(hasActiveUploads(data))
          const activeIds = getActiveUploadIds(data)
          const hasFinished = hasFinishedUploadsOnPage(
            activeUploadsRef.current,
            page,
            activeIds
          )
          activeUploadsRef.current = { page, ids: activeIds }
          if (hasFinished) {
            onJobsFinished?.()
          }
          // The rows already carry the total, so the count needs no request of its own
          queryClient.setQueryData(
            [CENSUS_UPLOAD_QUEUE_COUNT_QUERY_KEY, filters, refreshKey],
            meta.total ?? 0
          )
        } catch (error) {
          console.error(error)
          params.fail()
        }
      },
    }),
    [filters, refreshKey, queryClient, onJobsFinished]
  )

  // The grid is gone while the Queue is empty, so drop the stale handle
  useEffect(() => {
    if (total === 0) {
      setGrid(undefined)
      setIsPolling(false)
    }
  }, [total])

  // Moves the progress bars; Drafts is left alone and refreshes by hand
  useEffect(() => {
    if (!grid || !isPolling) {
      return undefined
    }
    const timer = setInterval(() => {
      grid.api.refreshServerSide({ purge: false })
    }, CENSUS_QUEUE_POLL_INTERVAL_MS)
    return () => clearInterval(timer)
  }, [grid, isPolling])

  useEffect(() => {
    if (grid) {
      grid.api.setGridOption('serverSideDatasource', datasource)
      grid.api.refreshServerSide({ purge: true })
    }
  }, [grid, datasource])

  useEffect(() => {
    if (grid) {
      grid.api.refreshServerSide({ purge: true })
    }
  }, [refreshKey])

  const onChanged = useCallback(() => {
    grid?.api.refreshServerSide({ purge: true })
    refetch()
  }, [grid, refetch])

  const columnDefs = useMemo(
    () =>
      getCensusQueueColDefs({
        clientNameLookup,
        onChanged,
        showAction,
        showChannel,
      }),
    [clientNameLookup, onChanged, showAction, showChannel]
  )

  if (total === 0) {
    return null
  }

  return (
    <>
      <Stack direction="row" align="center" className="subtitle">
        Queue
      </Stack>
      <AgGridReact
        rowStyle={{
          backgroundColor: 'white',
          marginBottom: '20px',
        }}
        className="censusReviewGrid ag-theme-alpine"
        getRowId={({ data }) => data.id}
        pagination
        paginationPageSize={10}
        cacheBlockSize={10}
        rowHeight={60}
        columnDefs={columnDefs}
        rowModelType="serverSide"
        onGridReady={(params) => setGrid(params)}
        domLayout="autoHeight"
        suppressHorizontalScroll
        paginationPageSizeSelector={[10, 20, 50, 100]}
      />
      <hr className="page-hr" style={{ marginTop: '10px' }} />
    </>
  )
}

export default CensusQueueGrid
